// import { useUser } from "components/providers/UserProvider"

import { Box, Checkbox, CircularProgress, TextField, Tooltip } from '@material-ui/core'
import { Button } from 'components/facets'
import { ArticleAttributes } from 'components/facets/ArticleAttributes'
import ImagePopover from 'components/facets/ImagePopover'
import { useCardData } from 'components/providers/CardDataProvider'
import CardmarketLogo from 'img/cardmarket-logo.svg'
import CardTraderLogo from 'img/cardtrader-logo.svg'
import { confirmRelistingsChoice, getOrderRelistingsData } from 'lib/api'
import { useRelistingStore } from 'lib/hooks/useRelistingStore'
import { getGameCodeFromName } from 'lib/utils'
import { useSnackbar } from 'notistack'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'
import { AutoSizer, Column, Table } from 'react-virtualized'
import {
  Article,
  Card,
  CardLanguage,
  ChannelFilterOptions,
  getGameFromId,
  InventoryArticle,
  mapBackendCountryEditionToInternal,
  mapBackendFinishAttributeToInternal,
} from 'shared'
import { getProductImageUrl } from 'utils/imageUtils'
import './relisting-presenter.css'
import { RelistingConfirmDialog } from './RelistingConfirmDialog'

export interface RelistingInterface {
  product: InventoryArticle
  orderRelistingId: number
  sourceOrderId: string
  cause: string
  ptProductId: number
  userChoice: string
  source: string
  quantity: number
}

export const RelistingPresenter = (): JSX.Element => {
  const { t } = useTranslation()
  const history = useHistory()
  const { enqueueSnackbar } = useSnackbar()
  const [windowWidth, setWindowWidth] = useState(window.innerWidth)
  const [windowHeight, setWindowHeight] = useState(window.innerHeight)
  const { listOfCardsToRelist, setListOfCardsToRelist, setShowRelistingsBanner } =
    useRelistingStore()
  const [listOfCardsWithFlagNotToRelist, setListOfCardsToRelistWithFlagNotToRelist] = useState<
    any[]
  >([])
  const [userChoices, setUserChoices] = useState<any[]>([])
  const [searchString, setSearchString] = useState('')
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false)
  const [isLoading, setIsLoading] = useState(true)

  const cardData = useCardData()

  const cardDataMap = useMemo(() => {
    const map = new Map<number, Card>()
    for (const card of cardData) {
      map.set(card._id, card)
    }
    return map
  }, [cardData])

  windowWidth
  windowHeight

  useEffect(() => {
    async function fetchRelistings() {
      setIsLoading(true)

      try {
        const response = await getOrderRelistingsData()

        response.sort((a: any, b: any) => a.ptProduct.mkmProductId - b.ptProduct.mkmProductId)

        const convertedResponse: RelistingInterface[] = []
        const prepareUserChoice = []

        for (const item of response) {
          const card = cardDataMap.get(item.ptProduct.mkmProductId)

          const article = new Article({
            idProduct: item.ptProduct.mkmProductId,
            quantity: item.quantity,
            condition: item.ptProduct.condition,
            idLanguage: item.ptProduct.idLanguage as CardLanguage,
            finishType: mapBackendFinishAttributeToInternal(item.ptProduct.finishType),
            isSigned: item.ptProduct.isSigned,
            isFirstEd: item.ptProduct.isFirstEd,
            countryEdition: mapBackendCountryEditionToInternal(item.ptProduct.countryEdition),
            gradingCompany: item.ptProduct.gradingCompany ?? undefined,
            grade: item.ptProduct.grade,
          })

          if (card) {
            convertedResponse.push({
              product: new InventoryArticle({
                attributes: article.attributes,
                card: card.toPlainObject(),
                ptArticleReservedQuantity: 0,
                location: article.comment,
                ptProductId: item.ptProduct._id,
              }),
              cause: item.cause,
              quantity: item.quantity,
              source: item.source,
              userChoice: item.userChoice,
              orderRelistingId: item.id,
              ptProductId: item.ptProduct._id,
              sourceOrderId: item.sourceOrderId,
            } as RelistingInterface)

            prepareUserChoice.push({ id: item.id, relisted: false })
          }
        }

        setListOfCardsToRelist(convertedResponse)
        setUserChoices(prepareUserChoice)

        // copy of the response processed with relisted marked as false.
        // this structure will be used in case the user does not want to relist anything
        setListOfCardsToRelistWithFlagNotToRelist(prepareUserChoice)
      } catch (error) {
        console.error(error)
        enqueueSnackbar(t('ptInventory.relisting.loadError'), { variant: 'error' })
      } finally {
        setIsLoading(false)
      }
    }

    fetchRelistings()

    function updateWindowDimensions() {
      setWindowWidth(window.innerWidth)
      setWindowHeight(window.innerHeight)
    }

    window.addEventListener('resize', updateWindowDimensions)
    return () => window.removeEventListener('resize', updateWindowDimensions)
  }, [])

  const handleSelectRow = (idRelist: number) => {
    setUserChoices((prev) =>
      prev.map((item) => (item.id === idRelist ? { ...item, relisted: !item.relisted } : item))
    )
  }

  const handleSelectAllRows = (event: React.ChangeEvent<HTMLInputElement>) => {
    const isChecked = event.target.checked

    // obtain visible ids of filtered list
    const visibleIds = new Set(filteredListOfCards.map((item) => item.orderRelistingId))

    setUserChoices((prev) =>
      prev.map((item) => (visibleIds.has(item.id) ? { ...item, relisted: isChecked } : item))
    )
  }

  /**
   * if the user has selected cards that he wants to save, then he is clicking on the 'confirm selections' button,
   * so the flag is true and a data structure is used that has been updated as the user checked the rows.
   * if the user has clicked on the 'don't save anything' button the flag must be false, and then a data structure
   * created by reading the response must be used where everything is set as not relist
   *
   * @param saveSelections indicates which data structure to use if the user wants to relist cards or not
   */
  const handleConfirmSelection = async (saveSelections: boolean) => {
    try {
      if (saveSelections) await confirmRelistingsChoice(userChoices)
      else await confirmRelistingsChoice(listOfCardsWithFlagNotToRelist)
      setShowRelistingsBanner(false)
      history.push('/')
      enqueueSnackbar(
        saveSelections
          ? t('orders.changesUploadedSuccessfully')
          : t('ptInventory.relisting.allDeletedSuccess'),
        { variant: 'success' }
      )
    } catch (error) {
      console.error(error)
    }
  }

  const orderImg = (source: ChannelFilterOptions) => {
    switch (source) {
      case ChannelFilterOptions.cardmarket:
        return (
          <img
            src={CardmarketLogo}
            className="blue-svg-logo"
            style={{
              height: '40px',
              width: 'auto',
            }}
          />
        )
      case ChannelFilterOptions.cardtrader:
        return (
          <img
            src={CardTraderLogo}
            className="blue-svg-logo"
            style={{
              height: '40px',
              width: 'auto',
            }}
          />
        )
      case ChannelFilterOptions.shopify:
        return <i className="fa-brands fa-shopify primary-color" style={{ fontSize: '40px' }} />
    }
  }

  const handleChangeSearchInput = (e: any) => {
    setSearchString(e.target.value)
  }

  const filteredListOfCards = useMemo(() => {
    if (!searchString) return listOfCardsToRelist

    return listOfCardsToRelist.filter((item) => {
      const name = item.product.card?.name?.toLowerCase() || ''
      const expansion = item.product.card?.expansion?.toLowerCase() || ''
      const code = item.product.card?.cn?.toString() || ''
      const set = item.product.card?.set?.toLowerCase() || ''
      const idOrder = item.sourceOrderId?.toString() || ''
      const search = searchString.toLowerCase() || ''

      return (
        name.includes(search) ||
        expansion.includes(search) ||
        code.includes(search) ||
        set.includes(search) ||
        idOrder.includes(search)
      )
    })
  }, [searchString, listOfCardsToRelist])

  const itemsToRelist = useMemo(() => {
    const idsToRelist = new Set(userChoices.filter((c) => c.relisted).map((c) => c.id))
    return listOfCardsToRelist.filter((item) => idsToRelist.has(item.orderRelistingId))
  }, [userChoices, listOfCardsToRelist])

  const headerCheckboxState = useMemo(() => {
    const visibleIds = new Set(filteredListOfCards.map((c) => c.orderRelistingId))
    const selectedVisibleCount = userChoices.filter(
      (c) => visibleIds.has(c.id) && c.relisted
    ).length

    const totalVisible = filteredListOfCards.length

    let allSelected = false
    let someSelected = false

    if (selectedVisibleCount > 0 && selectedVisibleCount < totalVisible) {
      someSelected = true
    } else if (selectedVisibleCount > 0 && selectedVisibleCount === totalVisible) {
      allSelected = true
    }

    return { allSelected, someSelected }
  }, [filteredListOfCards, userChoices])

  return (
    <div className="relisting-root p-lg-5 p-md-2">
      <div className="container-fluid p-xl-5">
        <div className="row">
          <div className="col-xl-6">
            <h2 className="secondary-color">{t('ptInventory.relisting.title')}</h2>
            <h4 className="pe-3">{t('ptInventory.relisting.descriptionOne')}</h4>
            <h4 className="pe-3">{t('ptInventory.relisting.descriptionTwo')}</h4>
            <h4 className="pe-3">{t('ptInventory.relisting.descriptionThree')}</h4>
            <h4
              className="pe-3 warning-color fw-semibold"
              data-testid="relisting-irreversible-notice"
            >
              {t('ptInventory.relisting.descriptionFour')}
            </h4>
            <div className="d-flex flex-row my-4">
              <Button
                variant="outlined"
                data-testid="dont-save"
                onClick={() => handleConfirmSelection(false)}
              >
                {t('ptInventory.relisting.dontSave')}
              </Button>
              <div className="ms-4">
                <Button
                  variant="contained"
                  data-testid="confirm-selection"
                  onClick={() => setConfirmDialogOpen(true)}
                  disabled={!userChoices.some((article) => article.relisted === true)}
                >
                  {t('ptInventory.relisting.confirmSelections')}
                </Button>
              </div>
            </div>
          </div>
          <div className="col-xl-6">
            <div className="d-flex py-1 flex-row w-100 mb-4 align-items-center">
              <Box alignSelf="center">
                <span>{t('table.search')}:</span>
              </Box>

              <Box height={45} className="d-flex align-items-start flex-column w-100 ms-2">
                <TextField
                  placeholder={t('orders.searchByNameCnExpansionOrderId')}
                  data-testid="filter-order-relisting"
                  fullWidth
                  disabled={isLoading}
                  onChange={handleChangeSearchInput}
                  variant="standard"
                />

                <span className={`fst-italic ${searchString === '' ? 'd-none' : 'd-block'}`}>
                  Filtered items: {filteredListOfCards.length}
                </span>
              </Box>
            </div>

            <div className="responsive-relistingTable-container">
              {isLoading ? (
                <div
                  className="d-flex flex-column align-items-center justify-content-center"
                  style={{ height: Math.max(windowHeight - 350, 500) }}
                  data-testid="relisting-table-loading"
                >
                  <CircularProgress size={80} />
                  <span className="mt-3">{t('common.loading')}</span>
                </div>
              ) : (
                <p className="fw-semibold">Total items: {listOfCardsToRelist.length}</p>
              )}
              {!isLoading && userChoices.length > 0 && listOfCardsToRelist.length > 0 && (
                <AutoSizer>
                  {({ width }) => (
                    <Table
                      width={Math.max(width, 600)}
                      height={Math.max(windowHeight - 350, 500)}
                      headerHeight={40}
                      rowHeight={112}
                      rowCount={filteredListOfCards.length}
                      rowGetter={({ index }) => filteredListOfCards[index]}
                      className={`relisting-table pb-5`}
                    >
                      <Column
                        width={60}
                        label=""
                        dataKey=""
                        disableSort={true}
                        headerRenderer={() => (
                          <div className="d-flex justify-content-center align-items-center w-100">
                            <Checkbox
                              data-testid="select-all"
                              checked={
                                headerCheckboxState.allSelected && filteredListOfCards.length > 0
                              }
                              indeterminate={
                                headerCheckboxState.someSelected && !headerCheckboxState.allSelected
                              }
                              onChange={handleSelectAllRows}
                            />
                          </div>
                        )}
                        cellRenderer={({ rowData: product, rowIndex }) => (
                          <div className="d-flex justify-content-center align-items-center w-100">
                            <Checkbox
                              data-testid={`select-${rowIndex}`}
                              checked={
                                userChoices.find((choice) => {
                                  return choice.id === product.orderRelistingId
                                }).relisted
                              }
                              onChange={() => handleSelectRow(product.orderRelistingId)}
                            />
                          </div>
                        )}
                      />

                      <Column
                        width={55}
                        label=""
                        dataKey=""
                        disableSort={true}
                        cellRenderer={({ rowData }) => (
                          <div className="d-flex justify-content-center align-items-center w-100">
                            <Tooltip title={getGameFromId(rowData.product.card.idGame) || ''}>
                              <span
                                className={`ss-${getGameCodeFromName(
                                  getGameFromId(rowData.product.card.idGame)
                                )} ss-game-icon ss-fw`}
                                style={{ fontSize: '1.5rem' }}
                              ></span>
                            </Tooltip>
                          </div>
                        )}
                      />

                      <Column
                        width={70}
                        label="Q.ty"
                        dataKey=""
                        disableSort={true}
                        cellRenderer={({ rowData: product }) => (
                          <div>
                            <span>{product.quantity}</span>
                          </div>
                        )}
                      />

                      <Column
                        width={400}
                        label="Card"
                        dataKey=""
                        disableSort={true}
                        cellRenderer={({ rowData: product }) => (
                          <div className="d-flex flex-row justify-content-start align-items-start">
                            <div className="me-2">
                              <ImagePopover
                                variant="imagePopover"
                                imageComponent={
                                  <img
                                    width="62px"
                                    src={getProductImageUrl(product.product.card?._id)}
                                  />
                                }
                                imageId={product.product.card?._id}
                              />
                            </div>
                            <div className="d-flex flex-column">
                              <span className="fw-semibold">{product.product.card?.name}</span>
                              <span>{product.product.card?.expansion}</span>
                              <span>
                                #{product.product.card?.cn} - {product.product.card?.set}
                              </span>

                              <div className="mt-2">
                                <div>
                                  <ArticleAttributes
                                    article={product.product}
                                    identifierForDataTestId={product.product.ptProductId + '-'}
                                    iconHeight={17}
                                  />
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                      />

                      <Column
                        width={150}
                        label="Order"
                        dataKey=""
                        disableSort={true}
                        cellRenderer={({ rowData: product }) => (
                          <div className="d-flex flex-row align-items-center">
                            {orderImg(product.source)}
                            <span className="ms-2">{product.sourceOrderId}</span>
                          </div>
                        )}
                      />
                    </Table>
                  )}
                </AutoSizer>
              )}
            </div>
          </div>
        </div>
      </div>

      <RelistingConfirmDialog
        open={confirmDialogOpen}
        itemsToRelist={itemsToRelist}
        onClose={() => setConfirmDialogOpen(false)}
        onConfirm={async () => {
          setConfirmDialogOpen(false)
          await handleConfirmSelection(true)
        }}
      />
    </div>
  )
}
